import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import OptimizedImage from '../../../components/ui/OptimizedImage';
import Button from '../../../components/ui/Button';
import AnimatedCounter from '../../../components/ui/AnimatedCounter';

const HeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-background via-muted/30 to-background">
      <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center px-4 py-2 bg-accent/10 border border-accent/20 rounded-full">
                <Icon name="AlertTriangle" size={16} className="text-accent mr-2" />
                <span className="font-caption text-sm text-accent font-medium">
                  Evaluación Gratuita Disponible
                </span>
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight">
                ¿Cuál es tu{' '}
                <span className="text-primary font-heading-bold">Edad Real</span>?
              </h1>
              
              <h2 className="font-heading text-2xl md:text-3xl text-accent leading-tight">
                Deja de mentirte a ti misma sobre tu salud
              </h2>
            </div>
            
            <div className="space-y-6">
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
                Si tienes entre 35 y 55 años y sientes que tu cuerpo te está traicionando, 
                es hora de enfrentar la verdad. Tu edad real podría ser{' '}
                <span className="text-accent font-body-semibold">10-15 años mayor</span>{' '}
                que tu edad cronológica.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <Link to="/edad-real-assessment-quiz" className="w-full sm:w-auto">
                  <Button
                    variant="default"
                    size="xl"
                    className="bg-accent hover:bg-accent/90 text-accent-foreground shadow-card-elevated hover-scale w-full sm:w-auto"
                    iconName="ClipboardList"
                    iconPosition="right"
                    iconSize={20}
                  >
                    <span className="font-body-semibold text-lg">
                      Descubre tu Edad Real AHORA
                    </span>
                  </Button>
                </Link>
                
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Icon name="Clock" size={16} />
                  <span className="font-caption text-sm">Solo 5 minutos</span>
                </div>
              </div>
            </div>
            
            {/* Social Proof */}
            <div className="flex flex-wrap items-center gap-6 pt-4 border-t border-border">
              <div className="flex items-center space-x-2">
                <div className="flex -space-x-2">
                  <Image
                    src="https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face"
                    alt="Usuario satisfecho"
                    className="w-8 h-8 rounded-full border-2 border-card"
                    loading="lazy"
                  />
                  <Image
                    src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face"
                    alt="Usuario satisfecho"
                    className="w-8 h-8 rounded-full border-2 border-card"
                    loading="lazy"
                  />
                  <Image
                    src="https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=40&h=40&fit=crop&crop=face"
                    alt="Usuario satisfecho"
                    className="w-8 h-8 rounded-full border-2 border-card"
                    loading="lazy"
                  />
                </div>
                <span className="font-caption text-sm text-muted-foreground">
                  +<AnimatedCounter
                    end={2847}
                    duration={2500}
                    className="font-caption text-sm text-muted-foreground"
                  />{' '}mujeres ya conocen su edad real
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex text-warning">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={16} className="fill-current" />
                  ))}
                </div>
                <span className="font-caption text-sm text-muted-foreground">4.9/5</span>
              </div>
            </div>
          </div>
          
          {/* Hero Video */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-card-elevated">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-[500px] md:h-[600px] object-cover"
                aria-label="Transformación de hábitos saludables - Miri Espejo Coach"
              >
                <source src="/assets/images/hero_video_habitos.mp4" type="video/mp4" />
                {/* Fallback image if video doesn't load */}
                <img
                  src="/assets/images/optimized/transformacion_habitos.webp"
                  alt="Transformación de hábitos saludables - Miri Espejo Coach"
                  className="w-full h-[500px] md:h-[600px] object-cover"
                />
              </video>
            </div>
            
            {/* Floating Stats - Hidden on mobile to prevent overflow */}
            <div className="hidden sm:block absolute -bottom-6 -left-6 bg-card rounded-xl shadow-card-elevated p-4 border border-border">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} className="text-accent-foreground" />
                </div>
                <div>
                  <div className="font-body-semibold text-lg text-foreground">
                    <AnimatedCounter end={87} duration={2000} suffix="%" />
                  </div>
                  <div className="font-caption text-xs text-muted-foreground">
                    Mejoran en 30 días
                  </div>
                </div>
              </div>
            </div>
            
            <div className="hidden sm:block absolute -top-6 -right-6 bg-card rounded-xl shadow-card-elevated p-4 border border-border">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Users" size={24} className="text-primary-foreground" />
                </div>
                <div>
                  <div className="font-body-semibold text-lg text-foreground">
                    + de <AnimatedCounter end={300} duration={2000} />
                  </div>
                  <div className="font-caption text-xs text-muted-foreground">
                    Mujeres transformadas
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;